'use client';

import { c as _c } from "react/compiler-runtime";
import { useInterval } from 'design-system/client';
import { pick } from 'lodash';
import { useTranslations, useLocale } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import Amount, { AmountCSSProperties } from '../Amount';
import { composeSelectedFontObject, getDataCssVariables } from '../helpers';
import ScreenName from '../ScreenName';
import StreambotWrapper from '../StreambotWrapper';
import { StreambotComponentProps } from '../types';
import WidgetHeadline from '../WidgetHeadline';
import getDefaultValues from './getDefaultValues';
import type { TopDonorWidgetSettings } from './topDonorWidgetSettingsSchema';
import { useApiV4WithDemoData } from '@/api';
import useGoogleFonts from '@/fonts/useGoogleFonts';
import { Maybe } from '@/types';
type Author = {
  name: Maybe<string>;
};
export type TopDonorData = {
  id: number;
  author: Maybe<Author>;
  donated_amount_in_cents?: number;
};
const defaultDemoData = [{
  id: 1,
  donated_amount_in_cents: 1500000,
  author: {
    name: 'Maxi M.'
  }
}, {
  id: 2,
  donated_amount_in_cents: 1000000,
  author: {
    name: 'ChatKnight'
  }
}, {
  id: 3,
  donated_amount_in_cents: 777700,
  author: {
    name: 'StreamSage'
  }
}] satisfies TopDonorData[];
type TopDonorProps = StreambotComponentProps<TopDonorWidgetSettings, TopDonorData[]>;
function useData(fundraisingEventId, t0, t1, demoData) {
  const $ = _c(11);
  const perPage = t0 === undefined ? 1 : t0;
  const maxCount = t1 === undefined ? 1 : t1;
  const [page, setPage] = useState(1);
  const [prevPerPage, setPrevPerPage] = useState(perPage);
  const locale = useLocale();
  let t2;
  if ($[0] !== demoData || $[1] !== page || $[2] !== perPage) {
    t2 = demoData?.slice((page - 1) * perPage, perPage * page);
    $[0] = demoData;
    $[1] = page;
    $[2] = perPage;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const slicedDemoData = t2;
  let t3;
  if ($[4] !== locale) {
    t3 = {
      locale
    };
    $[4] = locale;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      selector(param) {
        const data = param?.data;
        if (!data) {
          return null;
        }
        return data.map(_temp);
      },
      intervalMs: 100000
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const result = useApiV4WithDemoData(`/fundraising_events/${fundraisingEventId}/sum_donations.json?per_page=${perPage}&page=${page}`, t3, t4, slicedDemoData);
  let t5;
  if ($[7] !== maxCount) {
    t5 = () => {
      setPage(previous => {
        const next = previous + 1;
        return next <= maxCount ? next : 1;
      });
    };
    $[7] = maxCount;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const updatePage = t5;
  let t6;
  if ($[9] !== updatePage) {
    t6 = {
      callback: updatePage,
      intervalMs: 3000
    };
    $[9] = updatePage;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  useInterval(t6);
  if (prevPerPage !== perPage) {
    setPage(1);
    setPrevPerPage(perPage);
  }
  return result;
}
function _temp(opinion) {
  return pick(opinion, ["author", "id", "donated_amount_in_cents"]);
}
function DonationElement(t0) {
  const $ = _c(15);
  const {
    element,
    amountCssVariables,
    screenNameFont,
    screenNameFontColor,
    screenNameFontSize,
    screenNameFontWeight,
    displayMode
  } = t0;
  if (!element.id) {
    return null;
  }
  const t1 = displayMode === "list" ? "top-donor-list" : "top-donor-loop";
  const t2 = element.author?.name;
  const t3 = displayMode === "list" ? "screen-name-column" : undefined;
  let t4;
  if ($[0] !== screenNameFont || $[1] !== screenNameFontColor || $[2] !== screenNameFontSize || $[3] !== screenNameFontWeight || $[4] !== t2 || $[5] !== t3) {
    t4 = <ScreenName authorName={t2} screenNameFont={screenNameFont} screenNameFontColor={screenNameFontColor} screenNameFontSize={screenNameFontSize} screenNameFontWeight={screenNameFontWeight} className={t3} />;
    $[0] = screenNameFont;
    $[1] = screenNameFontColor;
    $[2] = screenNameFontSize;
    $[3] = screenNameFontWeight;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== amountCssVariables || $[8] !== element.donated_amount_in_cents) {
    t5 = <Amount amount={element.donated_amount_in_cents} className="listAmount" style={amountCssVariables} />;
    $[7] = amountCssVariables;
    $[8] = element.donated_amount_in_cents;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== element.id || $[11] !== t1 || $[12] !== t4 || $[13] !== t5) {
    t6 = <div key={element.id} className={t1}>{t4}{t5}</div>;
    $[10] = element.id;
    $[11] = t1;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function TopDonor(t0) {
  const $ = _c(82);
  let className;
  let fundraisingEventId;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      fundraisingEventId,
      className,
      demoData: t1,
      withDemoData: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = fundraisingEventId;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    className = $[1];
    fundraisingEventId = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const demoData = t1 === undefined ? defaultDemoData : t1;
  const withDemoData = t2 === undefined ? false : t2;
  let t3;
  if ($[6] !== props) {
    t3 = getDefaultValues(props);
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const mergedProps = t3;
  let dataFont;
  let dataFontColor;
  let dataFontSize;
  let dataFontWeight;
  let displayAlignment;
  let displayMode;
  let displayNumber;
  let headlineFont;
  let headlineFontColor;
  let headlineFontSize;
  let headlineFontWeight;
  let headlineText;
  let screenNameFont;
  let screenNameFontColor;
  let screenNameFontSize;
  let screenNameFontWeight;
  let wrapperProps;
  if ($[8] !== mergedProps) {
    ({
      displayMode,
      displayNumber,
      screenNameFont,
      screenNameFontColor,
      screenNameFontSize,
      screenNameFontWeight,
      dataFont,
      dataFontColor,
      dataFontSize,
      dataFontWeight,
      headlineText,
      headlineFont,
      headlineFontWeight,
      headlineFontSize,
      headlineFontColor,
      displayAlignment,
      ...wrapperProps
    } = mergedProps);
    $[8] = mergedProps;
    $[9] = dataFont;
    $[10] = dataFontColor;
    $[11] = dataFontSize;
    $[12] = dataFontWeight;
    $[13] = displayAlignment;
    $[14] = displayMode;
    $[15] = displayNumber;
    $[16] = headlineFont;
    $[17] = headlineFontColor;
    $[18] = headlineFontSize;
    $[19] = headlineFontWeight;
    $[20] = headlineText;
    $[21] = screenNameFont;
    $[22] = screenNameFontColor;
    $[23] = screenNameFontSize;
    $[24] = screenNameFontWeight;
    $[25] = wrapperProps;
  } else {
    dataFont = $[9];
    dataFontColor = $[10];
    dataFontSize = $[11];
    dataFontWeight = $[12];
    displayAlignment = $[13];
    displayMode = $[14];
    displayNumber = $[15];
    headlineFont = $[16];
    headlineFontColor = $[17];
    headlineFontSize = $[18];
    headlineFontWeight = $[19];
    headlineText = $[20];
    screenNameFont = $[21];
    screenNameFontColor = $[22];
    screenNameFontSize = $[23];
    screenNameFontWeight = $[24];
    wrapperProps = $[25];
  }
  let t4;
  if ($[26] !== headlineFont || $[27] !== headlineFontWeight) {
    t4 = composeSelectedFontObject(headlineFont, headlineFontWeight);
    $[26] = headlineFont;
    $[27] = headlineFontWeight;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  let t5;
  if ($[29] !== screenNameFont || $[30] !== screenNameFontWeight) {
    t5 = composeSelectedFontObject(screenNameFont, screenNameFontWeight);
    $[29] = screenNameFont;
    $[30] = screenNameFontWeight;
    $[31] = t5;
  } else {
    t5 = $[31];
  }
  let t6;
  if ($[32] !== dataFont || $[33] !== dataFontWeight) {
    t6 = composeSelectedFontObject(dataFont, dataFontWeight);
    $[32] = dataFont;
    $[33] = dataFontWeight;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== t4 || $[36] !== t5 || $[37] !== t6) {
    t7 = [t4, t5, t6];
    $[35] = t4;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  const fonts = t7;
  let t8;
  if ($[39] !== fonts) {
    t8 = {
      fonts
    };
    $[39] = fonts;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  useGoogleFonts(t8);
  const t = useTranslations("nextjs.streambot");
  const {
    data
  } = useData(fundraisingEventId, displayMode === "list" ? Number(displayNumber) : 1, displayMode === "list" ? 1 : Number(displayNumber), withDemoData ? demoData : undefined);
  let t9;
  if ($[41] !== dataFont || $[42] !== dataFontColor || $[43] !== dataFontSize || $[44] !== dataFontWeight) {
    t9 = getDataCssVariables(dataFont, dataFontSize, dataFontWeight, dataFontColor);
    $[41] = dataFont;
    $[42] = dataFontColor;
    $[43] = dataFontSize;
    $[44] = dataFontWeight;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  const amountCssVariables = t9;
  if (!data) {
    return null;
  }
  let t10;
  if ($[46] !== displayAlignment) {
    t10 = {
      textAlign: displayAlignment
    };
    $[46] = displayAlignment;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  let t11;
  if ($[48] !== headlineFont || $[49] !== headlineFontColor || $[50] !== headlineFontSize || $[51] !== headlineFontWeight || $[52] !== headlineText) {
    t11 = <WidgetHeadline content={headlineText} headlineFont={headlineFont} headlineFontWeight={headlineFontWeight} headlineFontSize={headlineFontSize} headlineFontColor={headlineFontColor} />;
    $[48] = headlineFont;
    $[49] = headlineFontColor;
    $[50] = headlineFontSize;
    $[51] = headlineFontWeight;
    $[52] = headlineText;
    $[53] = t11;
  } else {
    t11 = $[53];
  }
  let t12;
  if ($[54] !== amountCssVariables || $[55] !== data.length || $[56] !== t) {
    t12 = !data.length && <div style={amountCssVariables} className="top-donor-loop listAmount">{t("placeholder")}</div>;
    $[54] = amountCssVariables;
    $[55] = data.length;
    $[56] = t;
    $[57] = t12;
  } else {
    t12 = $[57];
  }
  let t13;
  if ($[58] !== amountCssVariables || $[59] !== data || $[60] !== displayMode || $[61] !== screenNameFont || $[62] !== screenNameFontColor || $[63] !== screenNameFontSize || $[64] !== screenNameFontWeight) {
    let t14;
    if ($[66] !== amountCssVariables || $[67] !== displayMode || $[68] !== screenNameFont || $[69] !== screenNameFontColor || $[70] !== screenNameFontSize || $[71] !== screenNameFontWeight) {
      t14 = donation => <DonationElement key={donation.id} element={donation} amountCssVariables={amountCssVariables} screenNameFont={screenNameFont} screenNameFontColor={screenNameFontColor} screenNameFontSize={screenNameFontSize} screenNameFontWeight={screenNameFontWeight} displayMode={displayMode} />;
      $[66] = amountCssVariables;
      $[67] = displayMode;
      $[68] = screenNameFont;
      $[69] = screenNameFontColor;
      $[70] = screenNameFontSize;
      $[71] = screenNameFontWeight;
      $[72] = t14;
    } else {
      t14 = $[72];
    }
    t13 = data.map(t14);
    $[58] = amountCssVariables;
    $[59] = data;
    $[60] = displayMode;
    $[61] = screenNameFont;
    $[62] = screenNameFontColor;
    $[63] = screenNameFontSize;
    $[64] = screenNameFontWeight;
    $[65] = t13;
  } else {
    t13 = $[65];
  }
  let t14;
  if ($[73] !== t10 || $[74] !== t11 || $[75] !== t12 || $[76] !== t13) {
    t14 = <div className="truncate-with-ellipsis align-heading" style={t10}>{t11}{t12}{t13}</div>;
    $[73] = t10;
    $[74] = t11;
    $[75] = t12;
    $[76] = t13;
    $[77] = t14;
  } else {
    t14 = $[77];
  }
  let t15;
  if ($[78] !== className || $[79] !== t14 || $[80] !== wrapperProps) {
    t15 = <StreambotWrapper {...wrapperProps} className={className}>{t14}</StreambotWrapper>;
    $[78] = className;
    $[79] = t14;
    $[80] = wrapperProps;
    $[81] = t15;
  } else {
    t15 = $[81];
  }
  return t15;
}
export default TopDonor;